import { useState, useEffect, useCallback, useRef } from "react";
import { Card, Row, Col, Badge } from "react-bootstrap";
import { Shield } from "react-feather";
import { useToast } from "context/toast-context/ToastProvider";

import QuizTimer from "./QuizTimer";
import QuizProgress from "./QuizProgress";
import Pagination from "./QuizPagination";
import Questions from "./Questions";
import QuizPallete from "./QuizPallete";
import QuizNumbersPallete from "./QuizNumbersPallete";
import ReadyForQuiz from "../../components/ReadyForQuiz";
import ProctoringWarningModal from "../../components/proctoring/ProctoringWarningModal";
import useProctoring from "../../components/proctoring/useProctoring";

const QuizStart = ({
  modifyGlobalQuizStatus,
  getSelectedAnswers,
  getProctoringLogs,
  getQuestionTimeTracking,
  quiz,
  quizTime,
  maxAttempts,
  remainingAttempts,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 1;
  const [quizStatus, setQuizStatus] = useState("");
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [clickedQuestionIds, setClickedQuestionIds] = useState([]);
  const [showStartQuizScreen, setShowStartQuizScreen] = useState(true);
  const isProctored = quiz?.isProctored === true;
  const quizContainerRef = useRef(null);
  const { showToast } = useToast();
  const [activeViolation, setActiveViolation] = useState(null);
  const handleViolation = useCallback((type) => {
    if (type === "copyAttempt") {
      showToast("Copy/paste is disabled during a proctored quiz.", "warning");
    } else {
      setActiveViolation(type);
    }
  }, [showToast]);
  const { violationCounts, proctoringLogs, requestFullscreen, isContainerFullscreen } = useProctoring({
    enabled: isProctored && !showStartQuizScreen && quizStatus !== "finish" && quizStatus !== "timeout",
    onViolation: handleViolation,
  });
  const totalViolations = Object.values(violationCounts).reduce((sum, n) => sum + n, 0);
  const handleResumeQuiz = () => {
    if (isProctored) {
      requestFullscreen(quizContainerRef.current);
    }
    setActiveViolation(null);
  };
  const [shuffledQuestions, setShuffledQuestions] = useState(() => {
    if (quiz?.questions && quiz.questions.length > 0) {
      let shuffled = [...quiz.questions];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    }
    return [];
  });

  useEffect(() => {
    if (quiz?.questions && quiz.questions.length > 0 && shuffledQuestions.length === 0) {
      let shuffled = [...quiz.questions];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      setShuffledQuestions(shuffled);
    }
  }, [quiz?.questions, shuffledQuestions.length]);

  const activeQuestions = shuffledQuestions.length > 0 ? shuffledQuestions : (quiz?.questions || []);

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = activeQuestions.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );

  const nPages = Math.ceil(activeQuestions.length / recordsPerPage);

  // ── Time-per-question tracking ────────────────────────────────────────────
  // Keyed by question id (not page index) so a post-start shuffle never mixes up times.
  const [questionTimeSpent, setQuestionTimeSpent] = useState({});
  const activeQuestionStartRef = useRef(null); // { questionId, startedAt }
  const activeQuestionId = currentRecords[0]?.id;

  useEffect(() => {
    // Only track while the quiz is being actively taken — not on the start screen,
    // in review mode (which lists all questions at once), or after submission.
    if (showStartQuizScreen || isReviewMode || quizStatus === "finish" || quizStatus === "timeout") {
      return;
    }
    if (activeQuestionId === undefined) return;

    const prev = activeQuestionStartRef.current;
    if (prev && prev.questionId !== activeQuestionId) {
      const elapsedSeconds = (Date.now() - prev.startedAt) / 1000;
      setQuestionTimeSpent((p) => ({
        ...p,
        [prev.questionId]: (p[prev.questionId] || 0) + elapsedSeconds,
      }));
    }
    if (!prev || prev.questionId !== activeQuestionId) {
      activeQuestionStartRef.current = { questionId: activeQuestionId, startedAt: Date.now() };
    }
  }, [activeQuestionId, showStartQuizScreen, isReviewMode, quizStatus]);
  const handleQuestionNumberCLick = (page) => {
    setCurrentPage(page);
  };

  const trackQUestionClick = (question) => {
    setClickedQuestionIds((pre) => {
      return [...pre, question.id];
    });
  };

  const startQuizHandler = () => {
    if (isProctored) {
      requestFullscreen(quizContainerRef.current);
    }
    setQuizStatus("start");
    setShowStartQuizScreen(false);
  };

  const handleReviewQuestions = () => {
    setIsReviewMode(true);
  };

  const handleBackToQuiz = () => {
    setIsReviewMode(false);
  };

  const handleAnswerChange = (questionId, selectedValue) => {
    questionId = String(questionId);
    setSelectedAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: selectedValue,
    }));
  };

  useEffect(() => {
    if (quizStatus === "finish" || quizStatus === "timeout") {
      handleSaveAnswers();
    }
  }, [quizStatus]);

  const handleSaveAnswers = () => {
    // Flush the time accrued on whichever question was active when the quiz ended
    // (Finish click or timer expiry) before handing the map up to the parent.
    let finalTimeSpent = questionTimeSpent;
    const active = activeQuestionStartRef.current;
    if (active) {
      const elapsedSeconds = (Date.now() - active.startedAt) / 1000;
      finalTimeSpent = {
        ...questionTimeSpent,
        [active.questionId]: (questionTimeSpent[active.questionId] || 0) + elapsedSeconds,
      };
      setQuestionTimeSpent(finalTimeSpent);
      activeQuestionStartRef.current = null;
    }

    getSelectedAnswers(selectedAnswers);
    if (isProctored && getProctoringLogs) {
      getProctoringLogs(proctoringLogs);
    }
    if (isProctored && getQuestionTimeTracking) {
      getQuestionTimeTracking(finalTimeSpent);
    }
  };

  useEffect(() => {
    if (quizStatus) {
      modifyGlobalQuizStatus(quizStatus);
    }
  }, [quizStatus, modifyGlobalQuizStatus]);

  return (
    <div
      ref={quizContainerRef}
      className="proctored-quiz-container"
      style={
        isContainerFullscreen
          ? { backgroundColor: "#f1f5f9", minHeight: "100vh", overflowY: "auto", padding: "1.5rem" }
          : undefined
      }
    >
      {isProctored && !showStartQuizScreen && (
        <ProctoringWarningModal
          show={!!activeViolation}
          violationType={activeViolation}
          violationCount={totalViolations}
          onResume={handleResumeQuiz}
          container={quizContainerRef}
        />
      )}
      {isReviewMode ? (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <button className="btn btn-secondary" onClick={handleBackToQuiz}>
              Back to Quiz
            </button>
            {isProctored && (
              <Badge bg="warning" text="dark" className="d-flex align-items-center gap-1 px-3 py-2">
                <Shield size={14} />
                Proctoring Active{totalViolations > 0 ? ` - Warnings: ${totalViolations}` : ""}
              </Badge>
            )}
          </div>

          <Card className="mb-4">
            <Card.Body className="card-body-scroll">
              <h4 className="mb-3">Review Questions</h4>
              <div className="mt-5">
                <Questions
                  questions={activeQuestions}
                  selectedAnswers={selectedAnswers}
                  onAnswerChange={handleAnswerChange}
                  trackQUestionClick={trackQUestionClick}
                />
              </div>
            </Card.Body>
          </Card>

          <Pagination
            setQuizStatus={setQuizStatus}
            nPages={nPages}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            selectedAnswers={selectedAnswers}
            showPreviousButton={false}
            showNextButton={false}
            showFinishButton={true}
          />
        </div>
      ) : showStartQuizScreen ? (
        <ReadyForQuiz
          startQuizHandler={startQuizHandler}
          questions={quiz?.questions}
          passingPercentage={quiz?.passingPercentage}
          isProctored={isProctored}
          maxAttempts={maxAttempts}
          remainingAttempts={remainingAttempts}
        />
      ) : (
        <>
          <Row>
            <Col lg={12} md={12} sm={12}>
              <div className="justify-content-between align-items-center bg-primary d-flex p-2 px-15 ">
                <div className="text-center">
                  <h1 className=" text-light my-6">{quiz?.contentTitle}</h1>
                </div>
              </div>
            </Col>
          </Row>
          <div>
            <div className="d-flex justify-content-end my-4  ">
              {/* <button
                className="btn btn-outline-primary me-3"
                style={{
                  color: "var(--bs-primary)",
                  borderColor: "var(--bs-primary)",
                  borderWidth: "2px",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "white";
                  e.target.style.color = "var(--bs-primary)";
                  e.target.style.borderColor = "var(--bs-primary)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "white";
                  e.target.style.color = "var(--bs-primary)";
                  e.target.style.borderColor = "var(--bs-primary)";
                }}
                onClick={handleReviewQuestions}
              >
                Review Questions
              </button> */}
            </div>
            <div className="d-flex justify-content-between">
              <div style={{ width: "70%" }}>
                <Card className="mb-4">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-3">
                      <div className="d-flex align-items-center">
                        {isProctored && (
                          <Badge bg="warning" text="dark" className="d-flex align-items-center gap-1 px-3 py-2">
                            <Shield size={14} />
                            Proctoring Active{totalViolations > 0 ? ` - Warnings: ${totalViolations}` : ""}
                          </Badge>
                        )}
                      </div>
                      <div className="d-flex align-items-center">
                        <QuizTimer
                          hours={Math.floor(quizTime / 60)}
                          minutes={Math.floor(quizTime % 60)}
                          seconds={Math.round((quizTime % 1) * 60)}
                          setQuizStatus={setQuizStatus}
                        />
                      </div>
                    </div>

                    <QuizProgress
                      currentQuestion={indexOfFirstRecord + 1}
                      totalQuestion={activeQuestions.length}
                    />

                    <div className="mt-5">
                      <Questions
                        questions={currentRecords}
                        selectedAnswers={selectedAnswers}
                        onAnswerChange={handleAnswerChange}
                        trackQUestionClick={trackQUestionClick}
                        startIndex={indexOfFirstRecord + 1}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div style={{ width: "25%" }}>
                <Card className="mb-4">
                  <Card.Body>
                    <div>
                      <QuizPallete />
                    </div>
                    <div>
                      <QuizNumbersPallete
                        questions={activeQuestions}
                        clickedQuestionIds={clickedQuestionIds}
                        handleQuestionNumberCLick={handleQuestionNumberCLick}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </div>

            <Pagination
              setQuizStatus={setQuizStatus}
              nPages={nPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              selectedAnswers={selectedAnswers}
              saveAnswers={handleSaveAnswers}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default QuizStart;
